import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Send, X, Minus, MessageSquare, Wrench, ShieldAlert } from "lucide-react";

/**
 * SIXChat — shared React chat panel for the three faces.
 * Props:
 *   face: "concierge" | "copilot" | "ops"
 *   variant: "floating" | "inline"
 *   pageContext?: string (e.g. "/pricing", used by concierge)
 *   startOpen?: bool
 */
export default function SIXChat({ face, variant = "floating", pageContext, startOpen = false }) {
  const [status, setStatus] = useState(null);   // agent/status
  const [open, setOpen] = useState(startOpen);
  const [minimized, setMinimized] = useState(false);
  const [convId, setConvId] = useState(() => {
    return localStorage.getItem(`six_conv_${face}`) || null;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => setStatus({ llm_available: false }));
  }, []);

  useEffect(() => {
    // Load existing conversation on open, if we have an id
    if (open && convId && messages.length === 0) {
      api.get(`/agent/conversations/${convId}`).then((r) => {
        setMessages((r.data.messages || []).map((m) => ({
          role: m.role, content: m.content, tool_trace: m.tool_trace || [],
        })));
      }).catch(() => {
        // Stale conv id — reset
        localStorage.removeItem(`six_conv_${face}`);
        setConvId(null);
      });
    }
  }, [open, convId, messages.length, face]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true); setErr("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    try {
      const { data } = await api.post("/agent/chat", {
        face, text, conversation_id: convId, page_context: pageContext,
      });
      if (data.conversation_id && data.conversation_id !== convId) {
        setConvId(data.conversation_id);
        localStorage.setItem(`six_conv_${face}`, data.conversation_id);
      }
      setMessages((prev) => [...prev, {
        role: "assistant", content: data.assistant, tool_trace: data.tool_trace || [],
        offline: data.offline, budget_hit: data.budget_hit,
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(String(detail));
      setMessages((prev) => [...prev, { role: "system", content: `Agent error: ${detail}` }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, face, convId, pageContext]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const resetConv = () => {
    localStorage.removeItem(`six_conv_${face}`);
    setConvId(null); setMessages([]);
  };

  const disabled = !status?.llm_available || !status?.[`${face}_enabled`];

  const eyebrow = face === "concierge" ? "SIX · FRONT DESK"
    : face === "copilot" ? "SIX · COPILOT"
    : "SIX · OPS OPERATOR";

  // Suggested prompts per face — only shown before the first message.
  const suggestions = (
    face === "concierge" ? [
      "What is the DT6 Standard?",
      "How does pricing work?",
      "Am I eligible to be armed?",
      "Show me a live universe count.",
    ] : face === "copilot" ? [
      "Run a universe count for PA.",
      "Where is my last order?",
      "What is my usage this cycle?",
    ] : [
      "Daily brief.",
      "Triage the pipeline.",
      "Draft a follow-up.",
      "Deliver order …",
    ]
  );

  if (variant === "floating" && !open) {
    return (
      <button
        onClick={() => { setOpen(true); setMinimized(false); }}
        data-testid="six-launcher"
        className="fixed bottom-6 right-6 z-40 rounded-full px-4 py-3 shadow-lg flex items-center gap-2 font-heading font-bold transition-transform duration-200 hover:-translate-y-0.5"
        style={{ background: "var(--dt6-primary)", color: "#0B0E14" }}
      >
        <MessageSquare size={16} strokeWidth={2} />
        Ask SIX
      </button>
    );
  }

  const isFloating = variant === "floating";
  const panel = (
    <div
      className={`flex flex-col overflow-hidden ${isFloating ? "dt6-glass" : "dt6-card"}`}
      style={{
        background: isFloating ? undefined : "var(--dt6-surface)",
        borderRadius: isFloating ? 6 : undefined,
        width: isFloating ? 400 : "100%",
        maxWidth: isFloating ? "calc(100vw - 24px)" : "none",
        height: isFloating ? (minimized ? 56 : 620) : "100%",
        maxHeight: "calc(100vh - 24px)",
      }}
      data-testid={`six-chat-${face}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--dt6-border)" }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-sm flex items-center justify-center font-heading font-black" style={{ background: "var(--dt6-primary)", color: "#0B0E14" }}>6</div>
          <div className="min-w-0">
            <div className="font-mono text-[0.65rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">{eyebrow}</div>
            <div className="font-heading text-sm font-bold truncate">SIX — Data Desk</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {convId && (
            <button className="dt6-btn text-[0.65rem] py-1 px-2" onClick={resetConv} title="Start new conversation" data-testid={`six-reset-${face}`}>NEW</button>
          )}
          {variant === "floating" && (
            <>
              <button className="dt6-btn text-[0.65rem] p-1" onClick={() => setMinimized((v) => !v)} title="Minimize" data-testid={`six-min-${face}`}>
                <Minus size={12} />
              </button>
              <button className="dt6-btn text-[0.65rem] p-1" onClick={() => setOpen(false)} title="Close" data-testid={`six-close-${face}`}>
                <X size={12} />
              </button>
            </>
          )}
        </div>
      </div>

      {!minimized && (
        <>
          {/* Status banner */}
          {disabled && (
            <div className="px-4 py-2 text-xs font-mono tracking-[0.1em] uppercase" style={{ background: "var(--dt6-surface-2)", color: "var(--dt6-secondary)" }} data-testid={`six-offline-${face}`}>
              / AGENT OFFLINE — leave a message; a human will follow up.
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid={`six-messages-${face}`}>
            {messages.length === 0 && (
              <div className="text-sm text-[color:var(--dt6-muted)] space-y-4" data-testid="six-empty">
                <div>
                  <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">/ OPEN LINE</div>
                  {face === "concierge" ? "Ask about DT6 — modules, pricing, the free file audit, or getting your account set up." :
                    face === "copilot" ? "Ask about your orders, run a universe count, or check your usage." :
                    "Ask for the daily brief, triage the pipeline, draft follow-ups, run QA, or deliver an order."}
                </div>
                {!disabled && (
                  <div>
                    <div className="font-mono text-[0.65rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">/ SUGGESTED</div>
                    <div className="flex flex-wrap gap-2" data-testid={`six-suggestions-${face}`}>
                      {suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="dt6-prompt-chip"
                          onClick={() => setInput(s)}
                          data-testid={`six-suggest-${face}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => <Msg key={i} m={m} />)}
            {busy && (
              <div className="flex items-center gap-2 text-xs font-mono tracking-[0.1em] text-[color:var(--dt6-muted)]" data-testid="six-thinking">
                <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--dt6-primary)" }}></span>
                SIX IS WORKING...
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t px-3 py-3 flex items-end gap-2" style={{ borderColor: "var(--dt6-border)" }}>
            <textarea
              className="dt6-textarea flex-1 resize-none text-sm"
              rows={2}
              placeholder={disabled ? "Agent offline — leave a message." : "Message SIX..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={busy}
              data-testid={`six-input-${face}`}
            />
            <button
              className="dt6-btn dt6-btn-primary text-xs"
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid={`six-send-${face}`}
              title="Send (Enter)"
            >
              <Send size={12} />
            </button>
          </div>
          {err && <div className="px-3 pb-2 text-xs text-[color:var(--dt6-danger)]" data-testid={`six-err-${face}`}>{err}</div>}
        </>
      )}
    </div>
  );

  if (variant === "floating") {
    return <div className="fixed bottom-6 right-6 z-40" data-testid="six-panel-wrap">{panel}</div>;
  }
  return panel;
}

function Msg({ m }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="six-msg-user">
        <div className="dt6-card px-3 py-2 text-sm max-w-[85%]" style={{ background: "var(--dt6-surface-2)" }}>
          {m.content}
        </div>
      </div>
    );
  }
  if (m.role === "system") {
    return (
      <div className="text-xs text-[color:var(--dt6-danger)] px-2" data-testid="six-msg-system">
        {m.content}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2" data-testid="six-msg-assistant">
      <div className="w-6 h-6 rounded-sm flex items-center justify-center font-heading font-black shrink-0 mt-1" style={{ background: "var(--dt6-primary)", color: "#0B0E14", fontSize: 12 }}>6</div>
      <div className="max-w-[85%]">
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
        {m.tool_trace && m.tool_trace.length > 0 && (
          <div className="mt-2 space-y-1">
            {m.tool_trace.map((t, i) => (
              <div key={i} className="flex items-center gap-1 text-[0.65rem] font-mono tracking-[0.06em] text-[color:var(--dt6-muted)]" data-testid="six-tool-badge">
                {t.name === "flag_injection" ? (
                  <span className="dt6-chip dt6-chip-danger text-[0.6rem]"><ShieldAlert size={10} /> INJECTION BLOCKED</span>
                ) : (
                  <span className="dt6-chip text-[0.6rem]"><Wrench size={10} /> {t.name}{t.result?.ok === false ? " · REFUSED" : ""}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
